import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams, Link } from 'react-router-dom'
import { api } from '../services/api'
import { DeliverableCard } from '../components/DeliverableCard'

interface ProjectReport {
  id: string
  title: string
  period_start: string
  period_end: string
  report_type: string
  content_markdown?: string
  generated_by_name: string
  created_at: string
}

interface Storage {
  id: string
  storage_id: string
  name: string
  backend_type: string
}

interface Application {
  id: string
  app_id: string
  name: string
}

interface ProjectStorage {
  id: string
  storage: Storage
  path_filter: string | null
  added_at: string
}

interface ProjectApplication {
  id: string
  application: Application
  added_at: string
}

interface DeliverableStatusUpdate {
  id: string
  status: string
  note: string
  author_name: string
  author_email: string
  created_at: string
}

interface Evidence {
  id: string
  title: string
  evidence_type: 'file' | 'link' | 'commit' | 'activity'
  path: string | null
  relationship: 'contributes' | 'completes'
  status: 'suggested' | 'confirmed' | 'rejected'
  added_by: { id: string; name: string } | null
  added_at: string | null
}

interface ProjectDeliverable {
  id: string
  project_id: string
  title: string
  description: string | null
  due_date: string | null
  completed_at: string | null
  verification: string | null
  status: 'not_started' | 'in_progress' | 'complete'
  recent_updates?: DeliverableStatusUpdate[]
  evidence?: Evidence[]
}

interface Project {
  id: string
  project_id: string
  name: string
  description: string | null
  start_date: string | null
  end_date: string | null
  is_active: boolean
  storages?: ProjectStorage[]
  applications?: ProjectApplication[]
  deliverables?: ProjectDeliverable[]
}

interface ActivityAssignment {
  evidence_id: string
  deliverable_id: string
  deliverable_title: string | null
  relationship: 'contributes' | 'completes'
  status: 'confirmed' | 'suggested'
}

interface ActivityEvent {
  id: string  // Activity event ID from database
  timestamp: string
  event_type: string
  title: string
  description: string
  author_name: string
  author_email: string
  author_id: string
  author_avatar: string
  source_name: string
  source_type: string
  artifact_path: string
  url: string
  metadata: Record<string, unknown>
  assignment?: ActivityAssignment | null
}

interface ActivityResponse {
  events: ActivityEvent[]
  total: number
  sources: string[]
  synced: boolean  // Whether activity has been synced
  date_range: {
    start: string | null
    end: string | null
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function EventTypeIcon({ type }: { type: string }) {
  const icons: Record<string, string> = {
    commit: 'C',
    pr_opened: 'PR',
    pr_merged: 'M',
    pr_closed: 'X',
    file_created: '+',
    file_modified: '~',
  }
  return (
    <span className="w-6 h-6 rounded-full bg-[var(--color-surface)] flex items-center justify-center text-xs font-mono">
      {icons[type] || '?'}
    </span>
  )
}

function ProjectCard({ project }: { project: Project }) {
  const storageCount = project.storages?.length || 0
  const appCount = project.applications?.length || 0

  return (
    <Link
      to={`/projects/${project.project_id}`}
      className="card-hover no-underline"
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-medium">{project.name}</h3>
        {!project.is_active && (
          <span className="text-xs px-2 py-0.5 bg-[var(--color-surface)] rounded">
            Inactive
          </span>
        )}
      </div>
      {project.description && (
        <p className="text-sm text-[var(--color-text-secondary)] mb-3 line-clamp-2">
          {project.description}
        </p>
      )}
      <div className="flex gap-4 text-xs text-[var(--color-text-secondary)]">
        <span>{storageCount} storage</span>
        <span>{appCount} app{appCount !== 1 ? 's' : ''}</span>
        {(project.start_date || project.end_date) && (
          <span>
            {formatDate(project.start_date)} - {formatDate(project.end_date)}
          </span>
        )}
      </div>
    </Link>
  )
}


interface ActivityItemProps {
  event: ActivityEvent
  onAssign?: (event: ActivityEvent) => void
}

function ActivityItem({ event, onAssign }: ActivityItemProps) {
  const [expanded, setExpanded] = useState(false)

  const assignment = event.assignment

  return (
    <div className="card">
      <div className="flex gap-3">
        {/* Avatar/Icon */}
        <div className="flex-shrink-0">
          {event.author_avatar ? (
            <img
              src={event.author_avatar}
              alt={event.author_name}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[var(--color-surface)] flex items-center justify-center text-sm">
              {event.author_name[0]?.toUpperCase() || '?'}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header: WHO + WHEN */}
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{event.author_name}</span>
            <span className="text-[var(--color-text-secondary)]">
              {formatRelativeTime(event.timestamp)}
            </span>
          </div>

          {/* WHAT: Event type + Title */}
          <div className="flex items-center gap-2 mt-1">
            <EventTypeIcon type={event.event_type} />
            <a
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm hover:underline truncate"
            >
              {event.title}
            </a>
          </div>

          {/* WHERE: Source + Path */}
          <div className="flex items-center gap-2 mt-1 text-xs text-[var(--color-text-secondary)]">
            <span className="px-1.5 py-0.5 rounded bg-[var(--color-surface)]">
              {event.source_type}
            </span>
            <span>{event.source_name}</span>
            <span className="truncate">{event.artifact_path}</span>
          </div>

          {/* Assignment Status */}
          <div className="flex items-center gap-2 mt-2">
            {assignment ? (
              <button
                onClick={() => onAssign?.(event)}
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-[var(--color-accent)]/20 text-[var(--color-accent)] hover:bg-[var(--color-accent)]/30 transition-colors"
                title="Click to reassign"
              >
                <span className="opacity-70">→</span>
                <span className="truncate max-w-[200px]">{assignment.deliverable_title || 'Deliverable'}</span>
              </button>
            ) : (
              <button
                onClick={() => onAssign?.(event)}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-[var(--color-surface)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors"
              >
                <span>+</span>
                <span>Assign</span>
              </button>
            )}
          </div>

          {/* Expandable description */}
          {event.description && event.description !== event.title && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-[var(--color-text-secondary)] mt-2 hover:text-[var(--color-text)]"
            >
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          )}
          {expanded && event.description && (
            <pre className="mt-2 p-2 bg-[var(--color-surface)] rounded text-xs whitespace-pre-wrap max-h-48 overflow-auto">
              {event.description}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}

type ProjectTab = 'overview' | 'deliverables' | 'activity' | 'reports'

// Assignment Modal
interface AssignmentModalProps {
  event: ActivityEvent
  deliverables: ProjectDeliverable[]
  onClose: () => void
  onAssign: (deliverableId: string | null, relationship: 'contributes' | 'completes') => void
  isLoading?: boolean
}

function AssignmentModal({ event, deliverables, onClose, onAssign, isLoading }: AssignmentModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(event.assignment?.deliverable_id || null)
  const [relationship, setRelationship] = useState<'contributes' | 'completes'>(
    event.assignment?.relationship || 'contributes'
  )

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-medium mb-2">Assign Activity</h3>
        <p className="text-sm text-[var(--color-text-secondary)] mb-4 truncate" title={event.title}>
          {event.title}
        </p>

        <div className="space-y-2 mb-4">
          {/* Unassigned option */}
          <label
            className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-colors ${
              selectedId === null
                ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10'
                : 'border-[var(--color-border)] hover:border-[var(--color-text-secondary)]'
            }`}
          >
            <input
              type="radio"
              name="deliverable"
              checked={selectedId === null}
              onChange={() => setSelectedId(null)}
              className="sr-only"
            />
            <div className="flex-1">
              <span className="text-sm text-[var(--color-text-secondary)]">Unassigned</span>
            </div>
          </label>

          {/* Deliverable options */}
          {deliverables.map((d) => (
            <label
              key={d.id}
              className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-colors ${
                selectedId === d.id
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10'
                  : 'border-[var(--color-border)] hover:border-[var(--color-text-secondary)]'
              }`}
            >
              <input
                type="radio"
                name="deliverable"
                checked={selectedId === d.id}
                onChange={() => setSelectedId(d.id)}
                className="sr-only"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{d.title}</div>
                {d.due_date && (
                  <div className="text-xs text-[var(--color-text-secondary)]">
                    Due: {formatDate(d.due_date)}
                  </div>
                )}
              </div>
              {selectedId === d.id && (
                <span className="text-[var(--color-accent)]">✓</span>
              )}
            </label>
          ))}
        </div>

        {/* Relationship selector (only if assigned) */}
        {selectedId && (
          <div className="mb-4">
            <label className="block text-sm text-[var(--color-text-secondary)] mb-2">
              Relationship
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRelationship('contributes')}
                className={`flex-1 px-3 py-2 text-sm rounded border transition-colors ${
                  relationship === 'contributes'
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-text-secondary)]'
                }`}
              >
                Contributes
              </button>
              <button
                type="button"
                onClick={() => setRelationship('completes')}
                className={`flex-1 px-3 py-2 text-sm rounded border transition-colors ${
                  relationship === 'completes'
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-text-secondary)]'
                }`}
              >
                Completes
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
          >
            Cancel
          </button>
          <button
            onClick={() => onAssign(selectedId, relationship)}
            disabled={isLoading}
            className="btn btn-primary text-sm"
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ReportsSection({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [selectedReport, setSelectedReport] = useState<ProjectReport | null>(null)
  const [newReport, setNewReport] = useState({
    title: '',
    period_start: '',
    period_end: '',
    content_markdown: '',
  })

  // Set default date range (last 30 days)
  useState(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 30)
    setNewReport(p => ({
      ...p,
      period_start: start.toISOString().split('T')[0],
      period_end: end.toISOString().split('T')[0],
    }))
  })

  const { data: reports, isLoading } = useQuery({
    queryKey: ['reports', projectId],
    queryFn: () => api.get<ProjectReport[]>(`/projects/${projectId}/reports`),
  })

  const createMutation = useMutation({
    mutationFn: (params: typeof newReport) =>
      api.post<ProjectReport>(`/projects/${projectId}/reports`, params),
    onSuccess: (report) => {
      queryClient.invalidateQueries({ queryKey: ['reports', projectId] })
      setSelectedReport(report)
      setCreating(false)
      setNewReport(p => ({ ...p, title: '', content_markdown: '' }))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (reportId: string) =>
      api.delete(`/projects/${projectId}/reports/${reportId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', projectId] })
      setSelectedReport(null)
    },
  })

  const handleCreate = () => {
    if (!newReport.title || !newReport.period_start || !newReport.period_end || !newReport.content_markdown) {
      return
    }
    createMutation.mutate(newReport)
  }

  const handleViewReport = async (reportId: string) => {
    const report = await api.get<ProjectReport>(`/projects/${projectId}/reports/${reportId}`)
    setSelectedReport(report)
  }

  if (selectedReport) {
    return (
      <div>
        <button
          onClick={() => setSelectedReport(null)}
          className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] mb-4"
        >
          &larr; Back to reports
        </button>
        <div className="card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium">{selectedReport.title}</h3>
              <p className="text-sm text-[var(--color-text-secondary)]">
                {formatDate(selectedReport.period_start)} - {formatDate(selectedReport.period_end)}
              </p>
              <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                By {selectedReport.generated_by_name} on {formatDate(selectedReport.created_at)}
              </p>
            </div>
            <button
              onClick={() => deleteMutation.mutate(selectedReport.id)}
              className="text-xs text-red-400 hover:text-red-300"
            >
              Delete
            </button>
          </div>
          <div className="prose prose-invert prose-sm max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm bg-[var(--color-surface)] p-4 rounded">
              {selectedReport.content_markdown}
            </pre>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Create Report Form */}
      {creating ? (
        <div className="card">
          <h3 className="font-medium mb-4">New Report</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Title
              </label>
              <input
                type="text"
                value={newReport.title}
                onChange={(e) => setNewReport(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g., Monthly Progress Report - February 2026"
                className="input w-full"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Period Start
                </label>
                <input
                  type="date"
                  value={newReport.period_start}
                  onChange={(e) => setNewReport(p => ({ ...p, period_start: e.target.value }))}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Period End
                </label>
                <input
                  type="date"
                  value={newReport.period_end}
                  onChange={(e) => setNewReport(p => ({ ...p, period_end: e.target.value }))}
                  className="input w-full"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Content (Markdown)
              </label>
              <textarea
                value={newReport.content_markdown}
                onChange={(e) => setNewReport(p => ({ ...p, content_markdown: e.target.value }))}
                placeholder="Write your report content here..."
                className="input w-full font-mono text-sm"
                rows={12}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || !newReport.title || !newReport.content_markdown}
                className="btn btn-primary"
              >
                {createMutation.isPending ? 'Saving...' : 'Save Report'}
              </button>
              <button
                onClick={() => setCreating(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
            {createMutation.isError && (
              <p className="text-red-400 text-sm">Failed to save report</p>
            )}
          </div>
        </div>
      ) : (
        <button
          onClick={() => setCreating(true)}
          className="btn btn-primary"
        >
          + New Report
        </button>
      )}

      {/* Previous Reports */}
      <div>
        <h3 className="section-label">Reports</h3>
        {isLoading ? (
          <p className="text-[var(--color-text-secondary)]">Loading...</p>
        ) : reports && reports.length > 0 ? (
          <div className="space-y-2">
            {reports.map((r) => (
              <div key={r.id} className="card-hover cursor-pointer" onClick={() => handleViewReport(r.id)}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">{r.title}</span>
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      {formatDate(r.period_start)} - {formatDate(r.period_end)}
                    </p>
                  </div>
                  <div className="text-xs text-[var(--color-text-secondary)]">
                    {formatDate(r.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[var(--color-text-secondary)]">No reports yet.</p>
        )}
      </div>
    </div>
  )
}

function ProjectDetail({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<ProjectTab>('overview')
  const [dateRange, setDateRange] = useState<{
    start: string
    end: string
  }>({
    start: '',
    end: '',
  })
  const [assigningEvent, setAssigningEvent] = useState<ActivityEvent | null>(null)

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}?include_resources=true`),
  })

  const { data: activity, isLoading: activityLoading } = useQuery({
    queryKey: ['activity', projectId, dateRange],
    queryFn: () => {
      const params: Record<string, string> = {}
      if (dateRange.start) params.start_date = dateRange.start
      if (dateRange.end) params.end_date = dateRange.end
      return api.get<ActivityResponse>(`/projects/${projectId}/activity`, params)
    },
    enabled: !!project,
  })

  // Sync activity mutation
  const syncMutation = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/sync-activity`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity', projectId] })
    },
  })

  // Assign single activity mutation
  const assignMutation = useMutation({
    mutationFn: (params: { activity: ActivityEvent; deliverable_id: string | null; relationship: string }) =>
      api.post(`/projects/${projectId}/assign-activity`, {
        activity_event_id: params.activity.id,  // Use activity event ID
        activity: {
          type: params.activity.event_type,
          title: params.activity.title,
          path: params.activity.artifact_path || params.activity.url,
          content_preview: params.activity.description?.slice(0, 500),
          metadata: params.activity.metadata,
        },
        deliverable_id: params.deliverable_id,
        relationship: params.relationship,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity', projectId] })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      setAssigningEvent(null)
    },
  })

  if (projectLoading) {
    return <p className="text-[var(--color-text-secondary)]">Loading...</p>
  }

  if (!project) {
    return <p className="text-red-500">Project not found</p>
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <Link
            to="/projects"
            className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] mb-2 inline-block"
          >
            &larr; All Projects
          </Link>
          <h1 className="text-3xl">{project.name}</h1>
          {project.description && (
            <p className="text-[var(--color-text-secondary)] mt-2">
              {project.description}
            </p>
          )}
        </div>
        <div className="text-sm text-[var(--color-text-secondary)]">
          {project.start_date && (
            <div>Start: {formatDate(project.start_date)}</div>
          )}
          {project.end_date && <div>End: {formatDate(project.end_date)}</div>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[var(--color-border)]">
        {(['overview', 'deliverables', 'activity', 'reports'] as ProjectTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm capitalize border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? 'border-[var(--color-accent)] text-[var(--color-text)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'reports' ? (
        <ReportsSection projectId={projectId} />
      ) : activeTab === 'deliverables' ? (
        <section>
          <h2 className="section-label">Deliverables</h2>
          {project.deliverables && project.deliverables.length > 0 ? (
            <div className="space-y-2">
              {project.deliverables.map((d) => (
                <DeliverableCard
                  key={d.id}
                  deliverable={d}
                  type="project"
                  onUpdate={() => queryClient.invalidateQueries({ queryKey: ['project', projectId] })}
                />
              ))}
            </div>
          ) : (
            <p className="text-[var(--color-text-secondary)]">No deliverables defined.</p>
          )}
        </section>
      ) : activeTab === 'activity' ? (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-label mb-0">Activity Log</h2>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
                className="btn text-sm flex items-center gap-1.5"
                title="Sync activity from connected storages"
              >
                {syncMutation.isPending ? (
                  <>Syncing...</>
                ) : (
                  <>
                    <span>Sync</span>
                  </>
                )}
              </button>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) =>
                  setDateRange((r) => ({ ...r, start: e.target.value }))
                }
                className="input text-sm"
                placeholder="Start"
              />
              <span className="text-[var(--color-text-secondary)]">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) =>
                  setDateRange((r) => ({ ...r, end: e.target.value }))
                }
                className="input text-sm"
                placeholder="End"
              />
            </div>
          </div>

          {syncMutation.isError && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-400">
              Failed to sync activity. Please try again.
            </div>
          )}

          {/* Show sync prompt if no activity */}
          {!activityLoading && activity && !activity.synced && activity.events.length === 0 && (
            <div className="mb-4 p-4 bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-sm">
              <p className="mb-2">No activity synced yet. Click <strong>Sync</strong> to fetch activity from connected storages.</p>
              <button
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
                className="btn btn-primary text-sm"
              >
                {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          )}

          {activityLoading ? (
            <p className="text-[var(--color-text-secondary)]">
              Loading activity...
            </p>
          ) : activity?.events && activity.events.length > 0 ? (
            <>
              <div className="text-sm text-[var(--color-text-secondary)] mb-4">
                {activity.total} events from {activity.sources.join(', ')}
                {activity.date_range.start && (
                  <span>
                    {' '}
                    ({formatDate(activity.date_range.start)} -{' '}
                    {formatDate(activity.date_range.end)})
                  </span>
                )}
                <span className="ml-2">
                  • {activity.events.filter(e => e.assignment).length} assigned
                </span>
              </div>
              <div className="space-y-3">
                {activity.events.map((event, idx) => (
                  <ActivityItem
                    key={`${event.timestamp}-${idx}`}
                    event={event}
                    onAssign={setAssigningEvent}
                  />
                ))}
              </div>
            </>
          ) : (
            <p className="text-[var(--color-text-secondary)]">
              No activity found for the selected date range.
              {!project.start_date && !project.end_date && !dateRange.start && (
                <span> Set project dates or select a date range above.</span>
              )}
            </p>
          )}

          {/* Assignment Modal */}
          {assigningEvent && project.deliverables && (
            <AssignmentModal
              event={assigningEvent}
              deliverables={project.deliverables}
              onClose={() => setAssigningEvent(null)}
              onAssign={(deliverableId, relationship) => {
                assignMutation.mutate({
                  activity: assigningEvent,
                  deliverable_id: deliverableId,
                  relationship,
                })
              }}
              isLoading={assignMutation.isPending}
            />
          )}
        </section>
      ) : (
        /* Overview Tab */
        <>
          {/* Resources */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Storage */}
        <div>
          <h2 className="section-label">Storage</h2>
          {project.storages && project.storages.length > 0 ? (
            <div className="space-y-2">
              {project.storages.map((ps) => (
                <Link
                  key={ps.id}
                  to={`/files?storage=${ps.storage.id}${ps.path_filter ? `&path=${encodeURIComponent(ps.path_filter)}` : ''}`}
                  className="card-hover no-underline block"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--color-surface)]">
                      {ps.storage.backend_type}
                    </span>
                    <span>{ps.storage.name}</span>
                    <span className="text-xs text-[var(--color-text-secondary)] ml-auto">View files &rarr;</span>
                  </div>
                  {ps.path_filter && (
                    <div className="text-xs text-[var(--color-text-secondary)] mt-1">
                      Path: {ps.path_filter}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-[var(--color-text-secondary)]">
              No storage linked
            </p>
          )}
        </div>

        {/* Applications */}
        <div>
          <h2 className="section-label">Applications</h2>
          {project.applications && project.applications.length > 0 ? (
            <div className="space-y-2">
              {project.applications.map((pa) => (
                <Link
                  key={pa.id}
                  to="/apps"
                  className="card-hover no-underline block"
                >
                  <div className="flex items-center justify-between">
                    <span>{pa.application.name}</span>
                    <span className="text-xs text-[var(--color-text-secondary)]">Launch &rarr;</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-[var(--color-text-secondary)]">
              No applications linked
            </p>
          )}
        </div>
          </div>
        </>
      )}
    </div>
  )
}

export function Projects() {
  const [searchParams] = useSearchParams()
  const projectId = searchParams.get('id')

  // Check if we're on a detail view via URL path
  const pathParts = window.location.pathname.split('/')
  const pathProjectId = pathParts.length > 2 ? pathParts[2] : null

  const selectedProjectId = pathProjectId || projectId

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<Project[]>('/projects?include_resources=true'),
    enabled: !selectedProjectId,
  })

  // Detail view
  if (selectedProjectId) {
    return (
      <div className="max-w-4xl">
        <ProjectDetail projectId={selectedProjectId} />
      </div>
    )
  }

  // List view
  return (
    <div className="max-w-4xl">
      <h1 className="text-3xl mb-8">Projects</h1>

      {isLoading ? (
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      ) : projects && projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      ) : (
        <p className="text-[var(--color-text-secondary)]">
          No projects available. Contact your administrator to set up projects.
        </p>
      )}
    </div>
  )
}
